import request, { generateNotyMessage } from 'utils/request'
import { actions as notyActions } from 'layouts/ErrorBox'
import { downloadBlob } from 'utils/misc'
import { getFormattedDateString, today } from 'utils/time'
import { drawerActions } from 'store/drawer'
import { INSTRUMENT_TYPE_MAP } from 'utils/constants'
import { getStudyVersionID } from 'utils/studyConfigUtils'
import { _fetchParticipants } from '../../../../Participants/routes/ParticipantsPage/modules/Participants'
import { buildSurveyQuestionMap } from '../../../utils/surveyQuestions'
import getInstrumentDisplayName from '../../../utils/instrumentName'
import {
  canAssociateDcfInstance,
  canDownloadDcfCsv,
  canDownloadDcfPdf,
} from '../../../utils/dcfStatuses'
import formatDcfErrorMessage from '../../../utils/dcfErrorMessage'
import { buildDcfCsvContent } from '../../../utils/dcfCsvExport'

const SET_DCF_LIST = 'SET_DCF_LIST'
const SET_DCF_DETAIL = 'SET_DCF_DETAIL'
const SET_DCF_STATUSES = 'SET_DCF_STATUSES'
const SET_DCF_ACTIONS = 'SET_DCF_ACTIONS'
const SET_DCF_LOOKUPS = 'SET_DCF_LOOKUPS'
const RESET_DCF_LIST = 'RESET_DCF_LIST'

// Drops late list responses after a study switch / reset
let dcfListRequestStudyId = null

const setDcfList = list => ({
  type: SET_DCF_LIST,
  payload: list,
})

const setDcfDetail = detail => ({
  type: SET_DCF_DETAIL,
  payload: detail,
})

const setDcfStatuses = statuses => ({
  type: SET_DCF_STATUSES,
  payload: statuses,
})

const setDcfActions = actionsList => ({
  type: SET_DCF_ACTIONS,
  payload: actionsList,
})

const setDcfLookups = lookups => ({
  type: SET_DCF_LOOKUPS,
  payload: lookups,
})

const resetDcfList = () => {
  dcfListRequestStudyId = null
  return {
    type: RESET_DCF_LIST,
  }
}

export const ACTION_LABELS = {
  create_new_record: 'Create New Record',
  add_new_record: 'Add New Record',
  add_data_to_existing_record: 'Add data to existing record',
  flag_questions_only: 'Flag Questions Only',
}

const formatActionLabel = (action, actionsCatalog = []) => {
  if (!action) return '—'
  const fromCatalog = (actionsCatalog || []).find(item => item.code === action)
  return fromCatalog?.label || ACTION_LABELS[action] || action
}

const resolveSubjectId = (item, participantLookup = {}) => {
  if (item?.subject_id) return item.subject_id
  if (item?.participant_subject_id) return item.participant_subject_id
  const pid = item?.participant_id
  if (pid == null) return null
  return participantLookup[String(pid)] || null
}

const resolveInstrumentName = (item, instrumentLookup = {}) => {
  // Prefer the current display name; the stored name is a snapshot of the title.
  const iid = item?.instrument_id
  if (iid != null && instrumentLookup[String(iid)]) return instrumentLookup[String(iid)]
  if (item?.instrument_name) return item.instrument_name
  if (item?.instrument_title) return item.instrument_title
  return null
}

/**
 * Expected API response shape (thin dcf_requests row + S3 answers.json fields):
 * {
 *   dcf_requests: [{
 *     id, participant_id, subject_id, instrument_id, instrument_name,
 *     action, requested_by, status, created_at, ...
 *   }]
 * }
 */
const formatDcfList = (list = [], actionsCatalog = [], lookups = {}) => {
  const participantLookup = lookups.participants || {}
  const instrumentLookup = lookups.instruments || {}

  return list.map(item => {
    const {
      id,
      requested_by: requestedBy,
      created_at: createdAt,
      status,
      action,
    } = item

    const subjectId = resolveSubjectId(item, participantLookup)
    const instrumentName = resolveInstrumentName(item, instrumentLookup)
    const actionLabel = formatActionLabel(action, actionsCatalog)

    const row = [
      {
        key: 'serialNumber',
        value: '',
        className: 'serial-number-cell',
      },
      {
        key: 'actionType',
        value: actionLabel,
        sortValue: String(actionLabel),
      },
      {
        key: 'subjectId',
        value: subjectId ?? '—',
        sortValue: String(subjectId ?? ''),
      },
      {
        key: 'instrumentName',
        value: instrumentName ?? item.instrument_id ?? '—',
        sortValue: String(instrumentName ?? item.instrument_id ?? ''),
      },
      {
        key: 'requestedBy',
        value: requestedBy ?? '—',
        sortValue: String(requestedBy ?? ''),
      },
      {
        key: 'createdAt',
        value: createdAt ? getFormattedDateString(createdAt, 'MM/DD/YYYY', true) : '—',
        sortValue: String(createdAt ?? ''),
      },
      {
        key: 'status',
        value: status ?? '—',
        sortValue: String(status ?? ''),
      },
      {
        key: 'action',
        id,
        subjectId,
        instrumentName,
        status,
        dcfAction: action,
        participantId: item.participant_id,
        instrumentId: item.instrument_id,
        instrumentVersion: item.instrument_version || null,
        instanceId: item.instance_id || null,
        canDownload: canDownloadDcfPdf(status),
        canDownloadCsv: canDownloadDcfCsv(status),
        canAssociate: canAssociateDcfInstance(status),
      },
    ]
    row.id = id
    row.payload = item
    return row
  })
}

const fetchDcfLookups = studyID => {
  return (dispatch, getState) => {
    const { study, location } = getState()
    const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
    const studyVersionID = getStudyVersionID(location)
    const query = hasStudyPromotion ? `?study_version_id=${studyVersionID}` : ''

    const participantsPromise = dispatch(_fetchParticipants(studyID)).then(payload => {
      const map = {}
      Object.keys(payload || {}).forEach(ptpId => {
        const subjectId = payload[ptpId]?.subject_id
        if (subjectId) map[String(ptpId)] = subjectId
      })
      return map
    })

    const instrumentsPromise = dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${studyID}/instruments${query}`,
        errorMessage: 'Error fetching instruments for DCF list',
        success: payload => {
          // The instruments API returns either a bare array or { results: [...] }.
          const results = Array.isArray(payload) ? payload : payload?.results || []
          const map = {}
          results.forEach(item => {
            if (item?.type === INSTRUMENT_TYPE_MAP.announcement) return
            if (item?.id != null) {
              map[String(item.id)] = getInstrumentDisplayName(item)
            }
          })
          return map
        },
        fail: () => ({}),
        hasLoader: false,
      }),
    )

    return Promise.all([participantsPromise, instrumentsPromise]).then(
      ([participants, instruments]) => {
        const lookups = { participants, instruments }
        dispatch(setDcfLookups(lookups))
        return lookups
      },
    )
  }
}

const fetchDcfList = (studyID, { showLoader = true } = {}) => {
  return (dispatch, getState) => {
    dcfListRequestStudyId = String(studyID)
    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/studies/${studyID}/datacorrection`,
        errorMessage: 'Error fetching data correction requests',
        success: payload => {
          if (dcfListRequestStudyId !== String(studyID)) return null
          const list = payload?.dcf_requests || payload?.results || []
          const { dcfActions = [], dcfLookups = {} } = getState().dataCorrectionV2 || {}
          dispatch(setDcfList({ list, actionsCatalog: dcfActions, lookups: dcfLookups }))
          return list
        },
        hasLoader: showLoader,
        forceLoader: showLoader,
      }),
    )
  }
}

const fetchDcfStatuses = () => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/datacorrection/statuses`,
        errorMessage: 'Error fetching DCF statuses',
        success: payload => {
          const statuses = payload?.statuses || []
          dispatch(setDcfStatuses(statuses))
          return statuses
        },
        hasLoader: false,
      }),
    )
  }
}

const fetchDcfActions = () => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/datacorrection/actions`,
        errorMessage: 'Error fetching DCF actions',
        success: payload => {
          const actionsList = payload?.actions || []
          dispatch(setDcfActions(actionsList))
          return actionsList
        },
        hasLoader: false,
      }),
    )
  }
}

const fetchInstrumentSurvey = (studyID, instrumentId) => {
  return dispatch => {
    if (!studyID || !instrumentId) return Promise.resolve(null)
    return dispatch(
      request({
        method: 'GET',
        url: `/control/admin/studies/${studyID}/instruments/${instrumentId}/survey.json`,
        errorMessage: 'Error fetching survey questions for edit',
        success: payload => buildSurveyQuestionMap(payload || {}),
        fail: () => null,
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const downloadDcf = (studyID, dcfId) => {
  return dispatch => {
    const success = (blob, fileName) => {
      downloadBlob(blob, `study_${studyID}_dcf_${dcfId}_${today(true)}.pdf`, fileName)
    }
    const fail = () => {
      dispatch(
        notyActions.showError({
          text: generateNotyMessage('There was an error downloading the DCF PDF. Please try again.', false),
        }),
      )
    }

    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/pdf`,
        resType: 'blob',
        success,
        fail,
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const formatInstanceCandidates = (instances = []) =>
  instances.map(item => {
    const id = String(item.instance_id || item.id)
    return {
      id,
      startedBy: item.started_by ?? null,
      completedBy: item.completed_by ?? null,
      status: item.status ?? null,
      transferIgnore: item.transfer_ignore ?? false,
      reportingIgnore: item.reporting_ignore ?? false,
      payload: item,
    }
  })

/** Load all instances for Associate Instance (does not touch Create DCF state). */
const searchDcfInstances = (studyID, participantId, instrumentId) => {
  return dispatch => {
    if (studyID == null || participantId == null || instrumentId == null) {
      return Promise.reject(new Error('Missing study, participant, or instrument for instance search'))
    }
    const ptp = encodeURIComponent(String(participantId))
    const instrument = encodeURIComponent(String(instrumentId))

    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/studies/${studyID}/participants/${ptp}/instruments/${instrument}/instances`,
        errorMessage: 'Error loading instrument instances',
        success: payload => {
          const instances = payload?.instances || payload?.results || []
          return formatInstanceCandidates(instances)
        },
        hasLoader: true,
        forceLoader: true,
      }),
    ).then(result => {
      // request() resolves failed calls as true; normalize so the drawer can show an error.
      if (!Array.isArray(result)) return null
      return result
    })
  }
}

/**
 * Associate / replace instance on a DCF.
 * Accepts a string instanceId or
 * `{ instanceId, action, startBy, completedBy, transferIgnore, reportingIgnore }`.
 * For "create new", set action `create_new_record` and `instanceId: null` —
 * the instruments_instances row is created on Apply.
 */
const associateDcfInstance = (studyID, dcfId, instanceIdOrOptions) => {
  return dispatch => {
    const options =
      instanceIdOrOptions && typeof instanceIdOrOptions === 'object'
        ? instanceIdOrOptions
        : { instanceId: instanceIdOrOptions }
    const { instanceId, action, startBy, completedBy, transferIgnore, reportingIgnore } = options
    const body = {}
    if (Object.prototype.hasOwnProperty.call(options, 'instanceId')) {
      body.instance_id = instanceId
    }
    if (action) body.action = action
    if (startBy !== undefined) body.start_by = startBy
    if (completedBy !== undefined) body.completed_by = completedBy
    if (transferIgnore !== undefined) body.transfer_ignore = transferIgnore
    if (reportingIgnore !== undefined) body.reporting_ignore = reportingIgnore

    return dispatch(
      request({
        method: 'PATCH',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}`,
        body: JSON.stringify(body),
        errorMessage: 'Error associating instrument instance',
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          const actionLabel = ACTION_LABELS[action]
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage(
                action === 'create_new_record'
                  ? 'Action set to Create New Record. Instance will be created when the DCF is applied.'
                  : action === 'flag_questions_only' && instanceId
                    ? `Instance ${instanceId} associated. Action stays Flag Questions Only.`
                    : instanceId && actionLabel
                    ? `Instance ${instanceId} associated. Action set to ${actionLabel}.`
                    : instanceId
                      ? `Instance ${instanceId} associated with this DCF.`
                      : 'Instance association updated.',
              ),
            }),
          )
          return detail
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

/** Fetch PDF bytes for in-drawer e-Sign (no file download). */
const fetchDcfPdfBytes = (studyID, dcfId) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/pdf`,
        resType: 'blob',
        success: async blob => {
          const buffer = await blob.arrayBuffer()
          return new Uint8Array(buffer)
        },
        failMessage: 'Error loading DCF PDF for signing',
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const fetchReauthPubkey = () => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: '/dcf/signing/reauth-pubkey',
        failMessage: 'Unable to load signing public key',
        success: payload => payload,
        hasLoader: false,
      }),
    )
  }
}

const postSigningReauth = body => {
  return dispatch => {
    return dispatch(
      request({
        method: 'POST',
        url: '/dcf/signing/reauth',
        body: JSON.stringify(body),
        failMessage: 'Password verification failed',
        success: payload => payload,
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const uploadSignedDcfPdf = (studyID, dcfId, pdfBytes, reauthProof) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'PUT',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/pdf`,
        body: pdfBytes instanceof Blob ? pdfBytes : new Blob([pdfBytes], { type: 'application/pdf' }),
        contentType: 'application/pdf',
        headerOptions: {
          'X-DCF-Reauth-Proof': reauthProof,
        },
        failMessage: 'Error uploading signed DCF PDF',
        fail: () => {
          throw new Error('Error uploading signed DCF PDF')
        },
        success: payload => payload,
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const upsertDcfSignatures = (studyID, dcfId, signatures, options = {}) => {
  return dispatch => {
    const { pdfClientStamped = false, reauthProof } = options
    const headerOptions = {}
    if (reauthProof) headerOptions['X-DCF-Reauth-Proof'] = reauthProof
    return dispatch(
      request({
        method: 'PUT',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/signatures`,
        body: JSON.stringify({
          signatures,
          pdf_client_stamped: Boolean(pdfClientStamped),
        }),
        headerOptions,
        failMessage: 'Error saving DCF signatures',
        fail: () => {
          throw new Error('Error saving DCF signatures')
        },
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage('Signatures saved.'),
            }),
          )
          return detail
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const deleteDcfSignature = (studyID, dcfId, role) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'DELETE',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/signatures/${role}`,
        failMessage: 'Error removing DCF signature',
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage('Signature removed.'),
            }),
          )
          return detail
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const fetchDcfDetail = (studyID, dcfId, { showLoader = true } = {}) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}`,
        errorMessage: 'Error fetching DCF details',
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          return detail
        },
        hasLoader: showLoader,
        forceLoader: showLoader,
      }),
    )
  }
}

/** One CSV file per DCF; one row per answer (fetched from detail). */
const downloadDcfCsv = (studyID, dcfId) => {
  return dispatch => {
    return dispatch(fetchDcfDetail(studyID, dcfId)).then(detail => {
      if (!detail?.id) {
        dispatch(
          notyActions.showError({
            text: generateNotyMessage('Could not load DCF details for CSV export.', false),
          }),
        )
        return null
      }
      const csv = buildDcfCsvContent(detail)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      downloadBlob(blob, `study_${studyID}_dcf_${dcfId}_${today(true)}.csv`)
      return detail
    })
  }
}

const updateDcfStatus = (studyID, dcfId, status) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'PATCH',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/status`,
        body: JSON.stringify({ status }),
        errorMessage: 'Error updating DCF status',
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage(`DCF status updated to ${status}.`),
            }),
          )
          return detail
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const updateDcfContent = (studyID, dcfId, body) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'PATCH',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}`,
        body: JSON.stringify(body),
        errorMessage: 'Error updating DCF',
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          dispatch(
            notyActions.showSuccess({
              text: generateNotyMessage('DCF updated successfully.'),
            }),
          )
          return detail
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const applyDcf = (studyID, dcfId) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'POST',
        url: `/dcf/studies/${studyID}/datacorrection/${dcfId}/apply`,
        failMessage: 'Error applying DCF',
        success: payload => {
          const detail = payload?.dcf_request || payload
          dispatch(setDcfDetail(detail))
          const status = detail?.status || payload?.dcf_request?.status
          if (status === 'completed') {
            const inserted = payload?.answers_inserted ?? 0
            const updated = payload?.answers_updated ?? 0
            const created = payload?.instance_created ? ' New instance created.' : ''
            dispatch(
              notyActions.showSuccess({
                text: generateNotyMessage(
                  `DCF applied successfully.${created} Inserted ${inserted}, updated ${updated}.`,
                ),
              }),
            )
          } else {
            // Apply failures stay in approved — error_message is on the detail.
            dispatch(
              notyActions.showError({
                text: generateNotyMessage(
                  detail?.error_message ||
                    'DCF apply did not complete. Status remains approved — fix the issue and try again.',
                  false,
                ),
              }),
            )
          }
          return payload
        },
        fail: (_response, content) => {
          // Refresh so the persisted error_message appears on the review drawer.
          dispatch(fetchDcfDetail(studyID, dcfId, { showLoader: false }))
          dispatch(fetchDcfList(studyID, { showLoader: false }))
          const serverMessage = formatDcfErrorMessage(
            content?.message || content?.error_message,
            'DCF apply failed. Status remains approved — check the error on the review screen and try again.',
          )
          dispatch(
            notyActions.showError({
              text: generateNotyMessage(serverMessage, false),
            }),
          )
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const _defaultState = {
  dcfList: [],
  dcfDetail: null,
  dcfStatuses: [],
  dcfActions: [],
  dcfLookups: { participants: {}, instruments: {} },
}

const DataCorrectionV2 = (state = _defaultState, action) => {
  switch (action.type) {
    case SET_DCF_LIST: {
      const { payload } = action
      const list = Array.isArray(payload) ? payload : payload?.list || []
      const actionsCatalog = Array.isArray(payload)
        ? state.dcfActions
        : payload?.actionsCatalog || state.dcfActions
      const lookups = Array.isArray(payload)
        ? state.dcfLookups
        : payload?.lookups || state.dcfLookups
      return {
        ...state,
        dcfList: formatDcfList(list, actionsCatalog, lookups),
      }
    }
    case SET_DCF_DETAIL:
      return {
        ...state,
        dcfDetail: action.payload,
      }
    case SET_DCF_STATUSES:
      return {
        ...state,
        dcfStatuses: action.payload,
      }
    case SET_DCF_LOOKUPS: {
      const lookups = action.payload || { participants: {}, instruments: {} }
      const rawList = (state.dcfList || []).map(row => row.payload).filter(Boolean)
      return {
        ...state,
        dcfLookups: lookups,
        dcfList: rawList.length
          ? formatDcfList(rawList, state.dcfActions, lookups)
          : state.dcfList,
      }
    }
    case SET_DCF_ACTIONS: {
      const rawList = (state.dcfList || []).map(row => row.payload).filter(Boolean)
      return {
        ...state,
        dcfActions: action.payload,
        dcfList: rawList.length
          ? formatDcfList(rawList, action.payload, state.dcfLookups)
          : state.dcfList,
      }
    }
    case RESET_DCF_LIST:
      return _defaultState
    default:
      return state
  }
}

export default DataCorrectionV2

export const actions = {
  setDcfList,
  setDcfDetail,
  resetDcfList,
  fetchDcfList,
  fetchDcfLookups,
  fetchDcfStatuses,
  fetchDcfActions,
  fetchInstrumentSurvey,
  downloadDcf,
  downloadDcfCsv,
  searchDcfInstances,
  associateDcfInstance,
  fetchDcfPdfBytes,
  fetchReauthPubkey,
  postSigningReauth,
  uploadSignedDcfPdf,
  fetchDcfDetail,
  updateDcfStatus,
  updateDcfContent,
  upsertDcfSignatures,
  deleteDcfSignature,
  applyDcf,
  formatActionLabel,
  ACTION_LABELS,
  ...drawerActions,
}
